import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBed, faCheck, faSpinner, faArrowsRotate,
  faBroom, faStar, faWrench, faCircleCheck,
  faTriangleExclamation, faNoteSticky, faXmark,
  faMagnifyingGlass, faArrowRotateLeft, faWineBottle,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "./HousekeeperDashboard.css";
import MinibarModal from "../minibar/MinibarModal";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const STATUS_META: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  dirty:            { label: "Dirty",          color: "#f87171", bg: "rgba(239,68,68,0.12)",    icon: faTriangleExclamation },
  cleaning:         { label: "Cleaning",       color: "#fbbf24", bg: "rgba(251,191,36,0.12)",   icon: faBroom },
  clean:            { label: "Clean",          color: "#34d399", bg: "rgba(52,211,153,0.12)",   icon: faCircleCheck },
  inspected:        { label: "Inspected",      color: "#C9A84C", bg: "rgba(201,168,76,0.12)",   icon: faStar },
  "out-of-service": { label: "Out of Service", color: "rgba(242,235,217,0.3)", bg: "rgba(148,163,184,0.12)", icon: faWrench },
};

const FILTER_OPTIONS = ["all", "dirty", "cleaning", "clean", "inspected", "out-of-service"];

interface RoomTask {
  roomId: string;
  roomNumber: string;
  roomType: string | null;
  status: string;
  priority: string;
  assignedTo: string | null;
  notes: string | null;
  taskId: number | null;
}

export default function HousekeeperDashboard() {
  const [rooms, setRooms]           = useState<RoomTask[]>([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState("all");
  const [search, setSearch]         = useState("");
  const [saving, setSaving]         = useState<Record<string, boolean>>({});
  const [notesModal, setNotesModal]     = useState<RoomTask | null>(null);
  const [minibarRoom, setMinibarRoom]   = useState<RoomTask | null>(null);
  const [noteText, setNoteText]     = useState("");
  const [toast, setToast]           = useState("");

  // Bulk selection
  const [selected, setSelected]         = useState<Set<string>>(new Set());
  const [assignModal, setAssignModal]   = useState(false);
  const [assignName, setAssignName]     = useState("");
  const [bulkSaving, setBulkSaving]     = useState(false);

  const token   = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const load = useCallback(async (resetFilter = false) => {
    setLoading(true);
    if (resetFilter) setFilter("all");
    try {
      const { data } = await axios.get(`${API}/api/housekeeping`, { headers });
      setRooms(data || []);
    } catch {
      showToast("Failed to load rooms — check connection");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (room: RoomTask, newStatus: string) => {
    setSaving(s => ({ ...s, [room.roomId]: true }));
    try {
      if (room.taskId) {
        await axios.put(`${API}/api/housekeeping/${room.taskId}`, { status: newStatus }, { headers });
      } else {
        await axios.post(`${API}/api/housekeeping`, { roomid: room.roomId, status: newStatus }, { headers });
      }
      setRooms(prev => prev.map(r =>
        r.roomId === room.roomId ? { ...r, status: newStatus, taskId: r.taskId } : r
      ));
      showToast(`Room ${room.roomNumber} → ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch {
      showToast("Failed to update status");
    } finally {
      setSaving(s => ({ ...s, [room.roomId]: false }));
    }
  };

  const saveNote = async () => {
    if (!notesModal) return;
    setSaving(s => ({ ...s, [notesModal.roomId]: true }));
    try {
      if (notesModal.taskId) {
        await axios.put(`${API}/api/housekeeping/${notesModal.taskId}`, { notes: noteText }, { headers });
      } else {
        await axios.post(`${API}/api/housekeeping`, {
          roomid: notesModal.roomId, status: notesModal.status, notes: noteText,
        }, { headers });
      }
      setRooms(prev => prev.map(r =>
        r.roomId === notesModal.roomId ? { ...r, notes: noteText } : r
      ));
      showToast("Note saved");
      setNotesModal(null);
    } catch {
      showToast("Failed to save note");
    } finally {
      setSaving(s => ({ ...s, [notesModal!.roomId]: false }));
    }
  };

  const markAllClean = async () => {
    const dirty = rooms.filter(r => r.status === "dirty" || r.status === "cleaning");
    if (dirty.length === 0) { showToast("All rooms are already clean"); return; }
    showToast(`Marking ${dirty.length} rooms as clean…`);
    await Promise.all(dirty.map(room => updateStatus(room, "clean")));
    showToast(`${dirty.length} rooms marked as clean`);
  };

  const toggleSelect = (roomId: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(roomId) ? next.delete(roomId) : next.add(roomId);
      return next;
    });

  const toggleSelectAll = () => {
    if (selected.size === sorted.length) { setSelected(new Set()); }
    else { setSelected(new Set(sorted.map(r => r.roomId))); }
  };

  const bulkAssign = async () => {
    if (!assignName.trim()) return;
    setBulkSaving(true);
    const roomsToAssign = rooms.filter(r => selected.has(r.roomId));
    try {
      await Promise.all(roomsToAssign.map(room =>
        room.taskId
          ? axios.put(`${API}/api/housekeeping/${room.taskId}`, { assigned_to: assignName }, { headers })
          : axios.post(`${API}/api/housekeeping`, { roomid: room.roomId, status: room.status, assigned_to: assignName }, { headers })
      ));
      setRooms(prev => prev.map(r =>
        selected.has(r.roomId) ? { ...r, assignedTo: assignName } : r
      ));
      showToast(`${roomsToAssign.length} rooms assigned to ${assignName}`);
      setAssignModal(false);
      setAssignName("");
      setSelected(new Set());
    } catch {
      showToast("Failed to assign rooms");
    } finally {
      setBulkSaving(false);
    }
  };

  // Stats
  const counts = rooms.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Filtered list
  const filtered = rooms.filter(r => {
    if (filter !== "all" && r.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        String(r.roomNumber).toLowerCase().includes(q) ||
        (r.roomType ?? "").toLowerCase().includes(q) ||
        (r.notes ?? "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) =>
    String(a.roomNumber ?? "").localeCompare(String(b.roomNumber ?? ""), undefined, { numeric: true })
  );

  return (
    <DashboardLayout>
      <div className="hk-root">

        {/* Header */}
        <div className="hk-header">
          <div className="hk-header-left">
            <h1>
              <FontAwesomeIcon icon={faBroom} />
              Housekeeping
            </h1>
            <p className="hk-date">
              {new Date().toLocaleDateString("en-GB", {
                weekday: "long", day: "2-digit", month: "long", year: "numeric",
              })}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="hk-mark-all-btn" onClick={markAllClean}>
              <FontAwesomeIcon icon={faCircleCheck} /> Mark All Clean
            </button>
            <button className="hk-refresh-btn" onClick={() => load(true)}>
              <FontAwesomeIcon icon={faArrowsRotate} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="hk-stats">
          {Object.entries(STATUS_META).map(([key, m]) => (
            <div
              key={key}
              className={`hk-stat${filter === key ? " active" : ""}`}
              style={{
                background: m.bg,
                border: `1px solid ${m.color}33`,
                outlineColor: m.color,
                color: m.color,
              }}
              onClick={() => setFilter(filter === key ? "all" : key)}
            >
              <FontAwesomeIcon icon={m.icon} className="hk-stat-icon" />
              <span className="hk-stat-count">{counts[key] || 0}</span>
              <span className="hk-stat-label">{m.label}</span>
            </div>
          ))}
          <div className="hk-stat" style={{ background: "rgba(14,34,64,0.08)", border: "1px solid rgba(14,34,64,0.15)", color: "rgba(242,235,217,0.45)", cursor: "default" }}>
            <FontAwesomeIcon icon={faBed} className="hk-stat-icon" />
            <span className="hk-stat-count" style={{ color: "#F2EBD9" }}>{rooms.length}</span>
            <span className="hk-stat-label">Total</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="hk-toolbar">
          <div className="hk-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              placeholder="Search room number, type, notes…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <FontAwesomeIcon
                icon={faXmark}
                style={{ color: "rgba(242,235,217,0.45)", cursor: "pointer" }}
                onClick={() => setSearch("")}
              />
            )}
          </div>
          <div className="hk-filter-pills">
            {FILTER_OPTIONS.map(f => (
              <button
                key={f}
                className={`hk-pill${filter === f ? " active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {f === "all" ? "All" : STATUS_META[f]?.label || f}
              </button>
            ))}
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="hk-bulk-bar">
            <span className="hk-bulk-count">{selected.size} room{selected.size > 1 ? "s" : ""} selected</span>
            <button className="hk-bulk-btn hk-bulk-assign" onClick={() => setAssignModal(true)}>
              <FontAwesomeIcon icon={faUserCheck} /> Assign to Staff
            </button>
            <button className="hk-bulk-btn hk-bulk-clear" onClick={() => setSelected(new Set())}>
              <FontAwesomeIcon icon={faXmark} /> Clear
            </button>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="hk-loading">
            <FontAwesomeIcon icon={faSpinner} spin /><br />
            <p>Loading rooms…</p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="hk-empty">
            <FontAwesomeIcon icon={faCircleCheck} />
            <p>
              {search
                ? `No rooms match "${search}"`
                : filter !== "all"
                  ? `No ${STATUS_META[filter]?.label || filter} rooms right now`
                  : "All rooms are accounted for."}
            </p>
            {(filter !== "all" || search) && (
              <button
                className="hk-empty-clear"
                onClick={() => { setFilter("all"); setSearch(""); }}
              >
                Show all rooms
              </button>
            )}
          </div>
        ) : (
          <div className="hk-table-wrap">
            <table className="hk-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" className="hk-checkbox"
                      checked={selected.size === sorted.length && sorted.length > 0}
                      onChange={toggleSelectAll} />
                  </th>
                  <th>Room</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Notes / Requests</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(room => {
                  const meta = STATUS_META[room.status] || STATUS_META.dirty;
                  const busy = saving[room.roomId];
                  return (
                    <tr key={room.roomId} style={selected.has(room.roomId) ? { background: "rgba(201,168,76,0.06)" } : undefined}>
                      {/* Select */}
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" className="hk-checkbox"
                          checked={selected.has(room.roomId)}
                          onChange={() => toggleSelect(room.roomId)} />
                      </td>
                      {/* Room */}
                      <td>
                        <div className="hk-room-num">
                          <FontAwesomeIcon icon={faBed} style={{ color: "rgba(242,235,217,0.45)", marginRight: 7, fontSize: 13 }} />
                          {room.roomNumber}
                        </div>
                        {room.roomType && <div className="hk-room-type">{room.roomType}</div>}
                      </td>

                      {/* Status */}
                      <td>
                        <span
                          className="hk-badge"
                          style={{
                            background: meta.bg,
                            color: meta.color,
                            border: `1px solid ${meta.color}44`,
                          }}
                        >
                          <FontAwesomeIcon icon={meta.icon} />
                          {meta.label}
                        </span>
                      </td>

                      {/* Assigned To */}
                      <td>
                        {room.assignedTo
                          ? <span style={{ fontSize: 12, color: "#C9A84C", fontWeight: 600 }}>{room.assignedTo}</span>
                          : <span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12 }}>—</span>
                        }
                      </td>

                      {/* Notes */}
                      <td>
                        {room.notes ? (
                          <span className="hk-notes-text">
                            <FontAwesomeIcon icon={faNoteSticky} style={{ marginRight: 5, color: "rgba(242,235,217,0.45)" }} />
                            {room.notes}
                          </span>
                        ) : (
                          <span style={{ color: "#2d3748", fontSize: 12 }}>—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="hk-actions">
                          {room.status === "dirty" && (
                            <button
                              className="hk-action-btn hk-btn-clean"
                              disabled={busy}
                              onClick={() => updateStatus(room, "cleaning")}
                            >
                              {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faBroom} />}
                              Start Cleaning
                            </button>
                          )}
                          {room.status === "cleaning" && (
                            <button
                              className="hk-action-btn hk-btn-done"
                              disabled={busy}
                              onClick={() => updateStatus(room, "clean")}
                            >
                              {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                              Mark Clean
                            </button>
                          )}
                          {room.status === "clean" && (
                            <button
                              className="hk-action-btn hk-btn-inspect"
                              disabled={busy}
                              onClick={() => updateStatus(room, "inspected")}
                            >
                              {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faStar} />}
                              Inspected
                            </button>
                          )}
                          {(room.status === "inspected" || room.status === "out-of-service") && (
                            <button
                              className="hk-action-btn hk-btn-dirty"
                              disabled={busy}
                              onClick={() => updateStatus(room, "dirty")}
                            >
                              {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faArrowRotateLeft} />}
                              Mark Dirty
                            </button>
                          )}
                          <button
                            className="hk-action-btn hk-btn-minibar"
                            onClick={() => setMinibarRoom(room)}
                            title="Add minibar charges"
                          >
                            <FontAwesomeIcon icon={faWineBottle} />
                            Minibar
                          </button>
                          <button
                            className="hk-action-btn hk-btn-note"
                            onClick={() => { setNotesModal(room); setNoteText(room.notes || ""); }}
                          >
                            <FontAwesomeIcon icon={faNoteSticky} />
                            {room.notes ? "Edit Note" : "Add Note"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Bulk Assign Modal */}
        {assignModal && (
          <div className="hk-modal-overlay" onClick={() => setAssignModal(false)}>
            <div className="hk-modal hk-assign-modal" onClick={e => e.stopPropagation()}>
              <div className="hk-modal-header">
                <h3>
                  <FontAwesomeIcon icon={faUserCheck} />
                  Assign {selected.size} Room{selected.size > 1 ? "s" : ""}
                </h3>
                <button className="hk-modal-close" onClick={() => setAssignModal(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="hk-assign-room-list">
                {rooms.filter(r => selected.has(r.roomId)).map(r => (
                  <span key={r.roomId} className="hk-assign-room-tag">Room {r.roomNumber}</span>
                ))}
              </div>
              <input
                className="hk-assign-input"
                placeholder="Staff name or ID…"
                value={assignName}
                onChange={e => setAssignName(e.target.value)}
                autoFocus
              />
              <div className="hk-modal-footer">
                <button className="hk-modal-save" onClick={bulkAssign} disabled={bulkSaving || !assignName.trim()}>
                  {bulkSaving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faUserCheck} />}
                  {bulkSaving ? "Assigning…" : "Assign"}
                </button>
                <button className="hk-modal-cancel" onClick={() => setAssignModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {notesModal && (
          <div className="hk-modal-overlay" onClick={() => setNotesModal(null)}>
            <div className="hk-modal" onClick={e => e.stopPropagation()}>
              <div className="hk-modal-header">
                <h3>
                  <FontAwesomeIcon icon={faNoteSticky} />
                  Room {notesModal.roomNumber} — Note / Request
                </h3>
                <button className="hk-modal-close" onClick={() => setNotesModal(null)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <textarea
                rows={4}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="e.g. Needs extra towels, broken lamp in bathroom…"
              />
              <div className="hk-modal-footer">
                <button className="hk-modal-save" onClick={saveNote}>
                  <FontAwesomeIcon icon={faCheck} /> Save Note
                </button>
                <button className="hk-modal-cancel" onClick={() => setNotesModal(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Minibar Modal */}
        {minibarRoom && (
          <MinibarModal
            roomId={minibarRoom.roomId}
            roomNumber={minibarRoom.roomNumber}
            onClose={() => setMinibarRoom(null)}
          />
        )}

        {/* Toast */}
        {toast && <div className="hk-toast">{toast}</div>}

      </div>
    </DashboardLayout>
  );
}
